import React, { useCallback, useEffect, useState } from 'react';
import NewChat from 'gatorhelper';
import { useDispatch, useSelector } from 'react-redux';
import {
  billingActions, preChatActions, domainsActions, backupActions, sitesActions,
  commonActions,
  merlinActions,
} from '@/redux/modules';
import { COUNTRY, WHMCS_URL } from '@/config';
import useLocale from '@/hooks/useLocale';
import { useHistory, useLocation } from 'react-router-dom';
import { preChatAnalytics } from '@/analytics/preChat';
import { sidebarAnalytics } from '@/analytics/support/sidebar';
import ModalDomainNotContracted from '@/components/Domain/ModalDomainNotContracted';
import ModalDomainNotConfigured from '@/components/Domain/ModalDomainNotConfigured';
import { DuplicateOrdersModal } from '@/components/billing/components/DuplicateOrdersModal';
import { TicketUpgradeVpsModal } from '@/components/Tickets/TicketUpgradeVpsModal';
import {
  BRAVO_EMAIL_FLOW,
  BRAVO_CHANGE_PHONE_FLOW,
  ENABLE_SKILLED_DEPARTMENT,
  ASA_TEXTS,
  NEW_SUPPORT,
  CHARLIE_ENABLE_FINANCIAL_CHAT,
  CHARLIE_ENABLE_GENERAL_CHAT,
  CHARLIE_CHAT_HOLIDAYS,
  ENABLE_SUPPORT_SERVICE_CONTROL,
  ATIVACAO_SKIP_TICKET_TYPE_SELECTION,
  ATIVACAO_USE_AI_DEADLINE,
  ATIVACAO_N8N_INHOUSE_URLS,
  CHARLIE_WARNING_CHAT_TOOLTIP,
} from '@/config/GrowthBook/constants';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useDiagnostic } from '@/components/Layout/Menu/MyAlerts';
import logger from '@/utils/logger';
import withErrorBoundary from '@/utils/errorBoundary';
import { useTickets } from '@/hooks/tickets';
import { format, parseISO, isWithinInterval } from 'date-fns';
import { useChatbot } from '@/contexts/Chatbot';
import { useAiChat } from '@/hooks/AiChat';

const MODALS = {
  DOMAIN_NOT_CONTRACTED: 'DOMAIN_NOT_CONTRACTED',
  DOMAIN_NOT_CONFIGURED: 'DOMAIN_NOT_CONFIGURED',
  DUPLICATE_ORDERS: 'DUPLICATE_ORDERS',
};

let getDiagnosticInterval = null;
const PreChat = ({
  hasActiveChat,
  setWidgetIsOpen,
  handleStartZenDeskChat,
  widgetIsOpen,
}) => {
  const chatbot = useChatbot();

  const [modalToShow, setModalToShow] = useState(null);
  const [isClickBlocked, setIsClickBlocked] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { preChat: preChatLocale, routes, aichat: aiChatLocale } = useLocale();
  const { requestDiagnostic } = useDiagnostic();

  const preChatState = useSelector(state => state.preChat);
  const summaryState = useSelector(state => state.summary);
  const billingState = useSelector(state => state.billing);
  const { serverTime, serverTimeLoading } = useSelector(state => state.common);
  const { merlinUrl } = useSelector(state => state.merlin);

  const enableEmailVerify = useFeatureIsOn(BRAVO_EMAIL_FLOW);
  const enablePhoneVerify = useFeatureIsOn(BRAVO_CHANGE_PHONE_FLOW);
  const enableSkilledDepartment = useFeatureIsOn(ENABLE_SKILLED_DEPARTMENT);
  const enabledFinancialChat = useFeatureIsOn(CHARLIE_ENABLE_FINANCIAL_CHAT);
  const enabledGeneralChat = useFeatureIsOn(CHARLIE_ENABLE_GENERAL_CHAT);
  const enableDepartmentVerification = useFeatureIsOn(ENABLE_SUPPORT_SERVICE_CONTROL);
  const charlieWarningChatTooltip = useFeatureIsOn(CHARLIE_WARNING_CHAT_TOOLTIP);
  const skipTicketTypeSelection = useFeatureIsOn(ATIVACAO_SKIP_TICKET_TYPE_SELECTION);
  const usingAiDeadline = useFeatureIsOn(ATIVACAO_USE_AI_DEADLINE);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);

  const supportTimes = useFeatureValue(ASA_TEXTS);
  const holidaysObject = useFeatureValue(CHARLIE_CHAT_HOLIDAYS);
  const { routes: routesLocale } = useLocale();
  const { requestTickets } = useTickets({});
  const useNewSupport = useFeatureIsOn(NEW_SUPPORT);
  const [holiday, setHoliday] = useState({});
  const isOnboarding = location.pathname.includes('onboarding');
  const { triggerExternalMessage } = useAiChat();

  const checkHolidays = useCallback(() => {
    if (holidaysObject && holidaysObject.holidays && holidaysObject.holidays.length) {
      const now = serverTime ? parseISO(serverTime.iso8601) : new Date();
      const todayFormatted = format(now, 'yyyy-MM-dd');
      const currentHoliday = holidaysObject.holidays.find(holidayItem => holidayItem.days.includes(todayFormatted));

      if (currentHoliday) {
        const startGeneral = new Date(currentHoliday.startGeneral);
        const endGeneral = new Date(currentHoliday.endGeneral);

        const startFinancial = new Date(currentHoliday.startFinancial);
        const endFinancial = new Date(currentHoliday.endFinancial);

        const generalIsClosed = isWithinInterval(now, { start: startGeneral, end: endGeneral });
        const financialIsClosed = isWithinInterval(now, { start: startFinancial, end: endFinancial });

        if (generalIsClosed || financialIsClosed) {
          setHoliday({
            ...currentHoliday,
            closeGeneral: generalIsClosed ? currentHoliday.closeGeneral : false,
            closeFinancial: financialIsClosed ? currentHoliday.closeFinancial : false,
          });
        }
      }
    }
  }, [holidaysObject, serverTime]);

  useEffect(() => {
    checkHolidays();
  }, [checkHolidays]);

  const featureToggles = {
    enableSkilledDepartment,
    supportTimes,
    useNewSupport,
    enabledFinancialChat,
    enabledGeneralChat,
    holiday,
    enableDepartmentVerification,
    charlieWarningChatTooltip,
    skipTicketTypeSelection,
    usingAiDeadline,
  };

  const emailVerify = enableEmailVerify ? summaryState.emailVerified : true;
  const phoneVerify = enablePhoneVerify ? summaryState.phoneNumberVerified : true;
  const contactsVerified = emailVerify && phoneVerify;
  const newPendingOrdersList = preChatState.pendingOrders && preChatState.pendingOrders.filter(order => order.status === 'pending_analysis');
  const pendingOrdersList = newPendingOrdersList;

  const verifyDaysDifference = (date, days) => {
    try {
      const formatDate = new Date(date);
      const today = new Date();

      const daysDifference = (today - formatDate) / (1000 * 60 * 60 * 24);

      return daysDifference < days;
    } catch (e) {
      logger.error('Error on PreChat - method verifyDaysDifference', e);
    }
  };

  const hostingsList = preChatState.productsList.hostings && preChatState.productsList.hostings.filter(hosting => hosting.status !== 'Cancelled' || verifyDaysDifference(hosting.due_date, 30));
  const domainsList = preChatState.productsList.domains && preChatState.productsList.domains.filter(domain => domain.status !== 'Cancelled' || verifyDaysDifference(domain.due_date, 30));
  const productsList = { hostings: hostingsList, domains: domainsList };

  const getDiagnostic = useCallback((isOpening) => {
    try {
      requestTickets(true);
      isOpening && dispatch(preChatActions.productByStatus.request({ withCancelled: true, withPending: false }));
    } catch (e) {
      logger.error('Error on PreChat - method getDiagnostic', e);
    }
  }, [dispatch, requestTickets]);

  const handleSetScreenToShow = useCallback((screen) => {
    dispatch(preChatActions.preChat.setScreen(screen));
  }, [dispatch]);

  const getServerTime = useCallback(() => {
    try {
      dispatch(commonActions.serverTime.request());
    } catch (e) {
      logger.error('Error on PreChat - method getServerTime', e);
    }
  }, [dispatch]);

  const handleOpenChat = useCallback(isOpening => new Promise((resolve) => {
    if (!isClickBlocked) {
      if (!hasActiveChat) {
        try {
          if (!contactsVerified) {
            preChatState.screenToShowInPreChat !== 'problemSolved' && handleSetScreenToShow('triage');
            getDiagnostic(isOpening);
          }
          if (contactsVerified && preChatState && !preChatState.showPreChat) {
            preChatState.screenToShowInPreChat !== 'problemSolved' && handleSetScreenToShow('diagnostic');
            getDiagnostic(isOpening);
          }

          dispatch(preChatActions.preChat.setShowPreChat(isOpening));

          if (isOpening) {
            getServerTime();
            setIsClickBlocked(true);
            setTimeout(() => {
              setIsClickBlocked(false);
              resolve && resolve();
            }, 1550);
          } else {
            resolve && resolve();
          }
        } catch (e) {
          logger.error('Error on PreChat - method handleOpenChat', e);
          resolve && resolve();
        }
      } else {
        if (hasActiveChat && !widgetIsOpen) {
          // eslint-disable-next-line no-undef
          zE('messenger', 'open');
          setWidgetIsOpen(true);
        }

        if (hasActiveChat && widgetIsOpen) {
          // eslint-disable-next-line no-undef
          zE('messenger', 'close');
          setWidgetIsOpen(false);
        }
        resolve && resolve();
      }
    } else {
      resolve && resolve();
    }
  }), [contactsVerified, dispatch, getDiagnostic, handleSetScreenToShow, preChatState, hasActiveChat, isClickBlocked, getServerTime, setWidgetIsOpen, widgetIsOpen]);

  const merlinListener = useCallback((event) => {
    const action = event && event.data && event.data;
    if (typeof action === 'string') {
      const rules = {
        openSupport: () => {
          handleOpenChat().then(() => {
            setTimeout(() => {
              handleSetScreenToShow('chatDepartmentSelection');
            }, [100]);
          });
        },
      };
      action && rules[action] && rules[action]();
    }
  }, [
    handleOpenChat,
    handleSetScreenToShow,
  ]);

  const forceOpenSupport = useCallback(() => {
    if (preChatState.forceOpenSupport) {
      if (!chatbot.isActive) {
        dispatch(merlinActions.inhouse.renderInHouseChat(false));
      } else {
        chatbot.close();
      }
      dispatch(preChatActions.n8n.forceOpenSupport(({ bool: false, n8nParams: preChatState.n8nParams })));

      if (!preChatState.showPreChat) {
        handleOpenChat().then(() => {
          setTimeout(() => {
            handleSetScreenToShow('chatDepartmentSelection');
          }, [100]);
        });
      }
    }
  }, [
    handleOpenChat,
    handleSetScreenToShow,
    dispatch,
    preChatState.forceOpenSupport,
    preChatState.showPreChat,
    preChatState.n8nParams,
  ]);

  useEffect(() => {
    forceOpenSupport();
  }, [forceOpenSupport, preChatState.forceOpenSupport]);

  useEffect(() => {
    if (merlinUrl) {
      window.addEventListener('message', event => merlinListener(event));

      return () => {
        window.removeEventListener('message', event => merlinListener(event));
      };
    }
  }, [
    merlinListener,
    merlinUrl,
  ]);


  const handleClosePreChat = () => {
    dispatch(preChatActions.preChat.setShowPreChat(false));
    dispatch(preChatActions.preChat.setScreen('diagnostic'));
  };

  const goToChatTriage = () => {
    preChatAnalytics.goToChatTriage();
    dispatch(preChatActions.weekDay.get());
    handleSetScreenToShow('triage');
  };

  const redirectToSubscriptionManagement = ({ productId, productType }) => {
    try {
      const route = `${routes.subscriptions}${routes.manageSubscription}/${productId}/${productType}`;
      history.push(route);

      dispatch(preChatActions.preChat.setShowPreChat(false));

      setTimeout(() => {
        dispatch(preChatActions.preChat.setScreen('problemSolved'));
      }, 2000);
    } catch (e) {
      logger.error('Error on PreChat - method redirectToSubscriptionManagement', e);
    }
  };

  const handleCloseModal = () => {
    setModalToShow(null);
    handleSetScreenToShow('problemSolved');
    dispatch(preChatActions.preChat.setShowPreChat());
  };

  const goToTrustReactivationModal = (invoiceId) => {
    try {
      if (preChatState && !preChatState.trustReactivationInvoiceId) {
        dispatch(preChatActions.trustReactivation.setInvoiceId(invoiceId));
      }
      let trustReactivationPath = '';
      const subscriptionReactivationList = preChatState.trustReactivation.filter(subscription => subscription.invoice_id === preChatState.trustReactivationInvoiceId);
      if (subscriptionReactivationList.length === 1) {
        trustReactivationPath = `${routes.subscriptions}${routes.manageSubscription}/${subscriptionReactivationList[0].hosting_id}/hosting`;
      } else {
        trustReactivationPath = `${routes.subscriptions}`;
      }
      dispatch(preChatActions.trustReactivation.redirect(true));
      dispatch(preChatActions.preChat.setShowPreChat());
      history.push('/');
      setTimeout(() => {
        history.push(trustReactivationPath);
      }, 10);
    } catch (e) {
      logger.error('Error on PreChat - method goToTrustReactivationModal', e);
    }
  };

  const goToInvoicePaymentModal = (invoiceId) => {
    try {
      const unpaidInvoicePath = `${routes.invoices}${routes.unpaid}/${invoiceId}`;
      dispatch(preChatActions.preChat.setShowPreChat());
      dispatch(preChatActions.invoice.set.invoiceId(invoiceId));
      history.push(unpaidInvoicePath);
    } catch (e) {
      logger.error('Error on PreChat - method goToInvoicePaymentModal', e);
    }
  };

  const getDiagnosticProps = () => {
    const reloadDiagnostic = () => {
      requestDiagnostic('PreChat3');
      dispatch(preChatActions.diagnostic.loading(true));
      getDiagnostic(true);
    };

    const goToTos = () => {
      try {
        dispatch(preChatActions.preChat.setShowPreChat());
        dispatch(preChatActions.tos.redirect(true));

        if (preChatState.tos.tickets.length > 1) {
          if (location.pathname !== routes.help) {
            history.push(routes.help);
          }
          preChatAnalytics.tosModalDisplayed();
        } else if (preChatState.tos.tickets.length === 1) {
          const ticketPath = `${routes.ticket}/${preChatState.tos.tickets[0].id}`;
          if (location.pathname !== ticketPath) {
            history.push(ticketPath);
          }
        }
      } catch (e) {
        logger.error('Error on PreChat - method goToTos', e);
      }
    };

    const goToSubscriptionAnalysis = (productId, productType) => {
      try {
        redirectToSubscriptionManagement({ productId, productType });

        setTimeout(() => {
          dispatch(preChatActions.preChat.setShowPreChat());
        }, 2000);
      } catch (e) {
        logger.error('Error on PreChat - method goToSubscriptionAnalysis', e);
      }
    };

    const handleModalToShow = (modal) => {
      dispatch(preChatActions.preChat.setShowPreChat());
      setModalToShow(modal);
    };

    return ({
      loadingDiagnostic: preChatState.loading,
      requestSuccess: preChatState.diagnosticLoaded,
      helpLinks: preChatLocale.diagnostic.helpLinks,
      invoices: preChatState.invoices,
      refunds: preChatState.refunds,
      customer: preChatState.customer,
      domainsPropagation: preChatState.domainsPropagation,
      tosTickets: preChatState.tos.tickets,
      goToSubscriptionAnalysis,
      pendingOrders: pendingOrdersList,
      reloadDiagnostic,
      goToInvoicePaymentModal,
      setModalToShow: handleModalToShow,
      goToTos,
      goToDuplicateOrdersModal: () => handleModalToShow(MODALS.DUPLICATE_ORDERS),
      hasDuplicateOrders: preChatState.duplicateOrders && preChatState.duplicateOrders.length,
    });
  };

  const getTriageProps = () => {
    const goToSubscriptionCancelModal = (productId, productType) => {
      dispatch(preChatActions.preChat.setshowPreChat());
      try {
        redirectToSubscriptionManagement({ productId, productType });
        dispatch(preChatActions.subscription.setSubscriptionToCancel(productId));
        setTimeout(() => {
          dispatch(preChatActions.preChat.setScreen('problemSolved'));
        }, 1000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToSubscriptionCancelModal', e);
      }
    };

    const goToSubscriptionManagement = ({ productId, productType }) => {
      dispatch(preChatActions.preChat.setshowPreChat());
      try {
        redirectToSubscriptionManagement({ productId, productType });
        dispatch(preChatActions.subscription.redirectManageSubscription(true));
        setTimeout(() => {
          dispatch(preChatActions.preChat.setScreen('problemSolved'));
        }, 1000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToSubscriptionManagement', e);
      }
    };

    const goToPlanOrCycleChange = ({ planId }) => {
      dispatch(preChatActions.preChat.setshowPreChat());
      window.open(`${WHMCS_URL}/index.php?m=custom_upgrade&action=selectupgrade&id=${planId}`, '_blank');
      handleSetScreenToShow('problemSolved');
    };

    const goToUpgradeVPSModal = (productId) => {
      dispatch(preChatActions.preChat.setshowPreChat());
      try {
        dispatch(preChatActions.preChat.setOpen(false));
        dispatch(preChatActions.preChat.setshowPreChat());
        dispatch(preChatActions.vps.redirect(true));
        dispatch(preChatActions.vps.productId(productId));

        setTimeout(() => {
          dispatch(preChatActions.preChat.setScreen('problemSolved'));
        }, 2000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToUpgradeVPSModal', e);
      }
    };

    const goDirectToChangePaymentMethod = (productId, productType) => {
      dispatch(preChatActions.subscription.setSubscriptionToChangePaymentMethod(productId));

      redirectToSubscriptionManagement({ productId, productType });
    };

    const shouldFetchSubscriptions = billingState
      && billingState.subscriptions
      && billingState.subscriptions.items
      && !billingState.subscriptions.loaded
      && !billingState.subscriptions.loading
      && !billingState.subscriptions.items.length;

    const goToChangePaymentMethod = (productId, productType) => {
      dispatch(preChatActions.preChat.setshowPreChat());
      try {
        if (shouldFetchSubscriptions) {
          dispatch(billingActions.subscriptions.request({ preChatRedirect: () => goDirectToChangePaymentMethod(productId, productType) }));
        } else {
          goDirectToChangePaymentMethod(productId, productType);
        }
      } catch (e) {
        logger.error('Error on NewChatButton - method goToChangePaymentMethod', e);
      }
    };

    const goToHoldDomain = (invoiceId) => {
      try {
        dispatch(preChatActions.invoice.set.holdDomainRedirect(true));
        goToInvoicePaymentModal(invoiceId);
        dispatch(preChatActions.preChat.setshowPreChat());

        setTimeout(() => {
          dispatch(preChatActions.preChat.setScreen('problemSolved'));
        }, 2000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToHoldDomain', e);
      }
    };

    const requestSelectedProductDetails = (data) => {
      dispatch(preChatActions.productDetails.get(data));
    };

    const goToGenerateEppKey = (domainToGenKey) => {
      try {
        dispatch(preChatActions.eppKeyGen.onFlow(true));
        const domainDetailPath = `${routes.domains}${routes.domaindetail}/${domainToGenKey.domainId}`;
        history.push(domainDetailPath);
        dispatch(domainsActions.modalEppKey.open(domainToGenKey.domain, domainToGenKey.domainId));
        dispatch(preChatActions.preChat.setShowPreChat(false));

        setTimeout(() => {
          handleSetScreenToShow('problemSolved');
        }, 2000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToGenerateEppKey', e);
      }
    };

    const goToBackupModal = ({ id, category }) => {
      try {
        history.push(`${routesLocale.products}${routesLocale.productdetail}/${id}${routesLocale.backup}/wizard/${category}`);
        dispatch(preChatActions.preChat.setShowPreChat(false));

        dispatch(backupActions.set.redirectFromPreChat(true));
        setTimeout(() => {
          handleSetScreenToShow('problemSolved');
        }, 1000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToBackupModal', e);
      }
    };


    const goToSitesWizard = ({ domain }) => {
      try {
        history.push(`${routes.sitesPage}${routes.sitesCreate}/${domain}`);
        dispatch(preChatActions.preChat.setShowPreChat(false));

        dispatch(sitesActions.chat.setRedirectFromPreChat(true));
        setTimeout(() => {
          handleSetScreenToShow('problemSolved');
        }, 1000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToSitesWizard', e);
      }
    };

    const goToSslPage = ({ hostingId }) => {
      try {
        window.open(`${WHMCS_URL}/index.php?m=ssl_addon&action=request1&hosting_id=${hostingId}`, '_blank');
        dispatch(preChatActions.preChat.setshowPreChat());
        setTimeout(() => {
          handleSetScreenToShow('problemSolved');
        }, 1000);
      } catch (e) {
        logger.error('Error on NewChatButton - method goToSslPage', e);
      }
    };

    const datadog = {
      priority: () => logger.info('chat - departamento prioritário - clientarea'),
    };

    return ({
      goToSubscriptionCancelModal,
      startZendeskChat: handleStartZenDeskChat,
      productsList,
      trustReactivation: preChatState.trustReactivation,
      goToPlanOrCycleChange,
      goToUpgradeVPSModal,
      goToChangePaymentMethod,
      goDirectToChangePaymentMethod,
      goToGenerateEppKey,
      shouldFetchSubscriptions,
      goToHoldDomain,
      requestSelectedProductDetails,
      loadingSelectedProductDetails: preChatState.productDetailsLoading,
      selectedProductDetail: preChatState.productDetails,
      weekDay: preChatState.weekDay,
      goToBackupModal,
      goToSitesWizard,
      goToSslPage,
      goToSubscriptionManagement,
      datadog,
    });
  };

  const setGetDiagnosticInterval = useCallback(() => {
    const fifteenMinutes = 1000 * 60 * 30;
    clearInterval(getDiagnosticInterval);

    getDiagnosticInterval = setInterval(() => {
      requestDiagnostic('PreChat2');
    }, fifteenMinutes);
  }, [requestDiagnostic]);

  useEffect(() => {
    requestDiagnostic('PreChat1');
    setGetDiagnosticInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const resetDiagnosticInterval = useCallback(() => {
    if (preChatState.showPreChat) {
      setGetDiagnosticInterval();
    }
  }, [preChatState.showPreChat, setGetDiagnosticInterval]);

  useEffect(() => {
    resetDiagnosticInterval();
  }, [resetDiagnosticInterval]);


  const goToWizardDns = useCallback((id) => {
    try {
      history.push(`${routes.domains}${routes.dnsWizard}/${id}`);
      dispatch(preChatActions.reservedDomain.onFlow(true));

      dispatch(preChatActions.preChat.setShowPreChat(false));
      setTimeout(() => {
        handleSetScreenToShow('problemSolved');
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToWizardDns', e);
    }
  }, [dispatch, handleSetScreenToShow, history, routes]);

  const handleWidgetDisplay = useCallback((display) => {
    if (display) {
      // eslint-disable-next-line no-undef
      zE('messenger', 'open');
    } else {
      // eslint-disable-next-line no-undef
      zE('messenger', 'close');
    }
    setWidgetIsOpen(display);
  }, [setWidgetIsOpen]);

  const goToSitesWizard = () => {
    try {
      history.push(`${routes.sitesPage}${routes.sitesCreate}`);

      dispatch(sitesActions.chat.setRedirectFromPreChat(true));
      dispatch(preChatActions.preChat.setShowPreChat(false));
      setTimeout(() => {
        handleSetScreenToShow('problemSolved');
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToSitesWizard', e);
    }
  };

  const goToTicketsPage = (ticketType) => {
    try {
      if (sidebarAnalytics) {
        sidebarAnalytics.newSupportAnalytics('SUPPORT', 'SEE_TICKET');
      }

      if (typeof ticketType === 'string') {
        dispatch(preChatActions.tickets.setTicketType(ticketType !== 'frequent' ? ticketType : null));
        history.push(`${routes.help}${routes.supportforms}`);
      } else {
        history.push(routes.help);
      }

      dispatch(preChatActions.preChat.setShowPreChat(false));
      setTimeout(() => {
        handleSetScreenToShow('problemSolved');
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToTicketsPage', e);
    }
  };

  const goToGenerateEppKey = (domainToGenKey) => {
    try {
      dispatch(preChatActions.eppKeyGen.onFlow(true));
      const domainDetailPath = `${routes.domains}${routes.domaindetail}/${domainToGenKey.domainId}`;
      history.push(domainDetailPath);
      dispatch(domainsActions.modalEppKey.open(domainToGenKey.domain, domainToGenKey.domainId));
      dispatch(preChatActions.preChat.setShowPreChat(false));

      setTimeout(() => {
        handleSetScreenToShow('problemSolved');
      }, 2000);
    } catch (e) {
      logger.error('Error on PreChat - method goToGenerateEppKey', e);
    }
  };

  const goToChangePaymentMethod = (productId, productType) => {
    dispatch(preChatActions.preChat.setShowPreChat());

    const shouldFetchSubscriptions = billingState
      && billingState.subscriptions
      && billingState.subscriptions.items
      && !billingState.subscriptions.loaded
      && !billingState.subscriptions.loading
      && !billingState.subscriptions.items.length;

    const goDirectToChangePaymentMethod = (productId, productType) => {
      dispatch(preChatActions.subscription.setSubscriptionToChangePaymentMethod(productId));

      redirectToSubscriptionManagement({ productId, productType });
    };

    try {
      if (shouldFetchSubscriptions) {
        dispatch(billingActions.subscriptions.request({ preChatRedirect: () => goDirectToChangePaymentMethod(productId, productType) }));
      } else {
        goDirectToChangePaymentMethod(productId, productType);
      }
    } catch (e) {
      logger.error('Error on PreChat - method goToChangePaymentMethod', e);
    }
  };

  const goToPlanOrCycleChange = ({ planId }) => {
    dispatch(preChatActions.preChat.setShowPreChat());
    window.open(`${WHMCS_URL}/index.php?m=custom_upgrade&action=selectupgrade&id=${planId}`, '_blank');
    handleSetScreenToShow('problemSolved');
  };

  const goToSubscriptionCancelModal = (productId, productType) => {
    dispatch(preChatActions.preChat.setShowPreChat());
    try {
      redirectToSubscriptionManagement({ productId, productType });
      dispatch(preChatActions.subscription.setSubscriptionToCancel(productId));
      setTimeout(() => {
        dispatch(preChatActions.preChat.setScreen('problemSolved'));
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToSubscriptionCancelModal', e);
    }
  };

  const goToSubscriptionManagement = ({ productId, productType }) => {
    dispatch(preChatActions.preChat.setShowPreChat());
    try {
      redirectToSubscriptionManagement({ productId, productType });
      dispatch(preChatActions.subscription.redirectManageSubscription(true));
      setTimeout(() => {
        dispatch(preChatActions.preChat.setScreen('problemSolved'));
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToSubscriptionManagement', e);
    }
  };

  const goToBackupModal = ({ id, category }) => {
    try {
      history.push(`${routesLocale.products}${routesLocale.productdetail}/${id}${routesLocale.backup}/wizard/${category}`);
      dispatch(preChatActions.preChat.setShowPreChat(false));
      dispatch(backupActions.set.redirectFromPreChat(true));
      setTimeout(() => {
        handleSetScreenToShow('problemSolved');
      }, 1000);
    } catch (e) {
      logger.error('Error on PreChat - method goToBackupModal', e);
    }
  };


  const handleTalkWithAi = () => {
    handleClosePreChat();

    const route = wssToggleRoutes.general;
    dispatch(merlinActions.inhouse.setRoute(route));
    const getDiagnostic = aiChatLocale.options.find(item => item.action === 'diagnostic');
    triggerExternalMessage({ message: getDiagnostic.initialMessage });
  };

  return (
    <>
      <NewChat
        application={useNewSupport ? 'clientareav2' : 'clientarea'}
        brand={COUNTRY}
        diagnosticProps={getDiagnosticProps()}
        triageProps={getTriageProps()}
        handleOpenChat={isOpening => handleOpenChat(isOpening)}
        chatIsOpen={preChatState.showPreChat}
        screenToShow={preChatState.screenToShowInPreChat}
        contactsVerified={contactsVerified}
        setScreenToShow={handleSetScreenToShow}
        closePreChat={handleClosePreChat}
        featureToggles={featureToggles}
        analyticEvents={{ ...preChatAnalytics, ...sidebarAnalytics }}
        goToChatTriage={goToChatTriage}
        goToTrustReactivationModal={goToTrustReactivationModal}
        goToWizardDns={goToWizardDns}
        hideButton={!hasActiveChat}
        serverTime={serverTime}
        hasActiveChat={hasActiveChat}
        handleWidgetDisplay={handleWidgetDisplay}
        goToSitesWizard={goToSitesWizard}
        goToTicketsPage={goToTicketsPage}
        getServerTime={getServerTime}
        productsList={productsList}
        startZendeskChat={handleStartZenDeskChat}
        trustReactivation={preChatState.trustReactivation}
        goToGenerateEppKey={goToGenerateEppKey}
        goToChangePaymentMethod={goToChangePaymentMethod}
        goToPlanOrCycleChange={goToPlanOrCycleChange}
        goToSubscriptionCancelModal={goToSubscriptionCancelModal}
        goToSubscriptionManagement={goToSubscriptionManagement}
        goToBackupModal={goToBackupModal}
        serverTimeLoading={serverTimeLoading}
        departmentsStatus={(serverTime && serverTime.departments) || []}
        isOnboarding={isOnboarding}
        handleTalkWithAi={handleTalkWithAi}
        withN8nParams={preChatState.n8nParams}
      />

      {modalToShow === MODALS.DOMAIN_NOT_CONTRACTED && <ModalDomainNotContracted hideModal={handleCloseModal} isPreChat />}
      {modalToShow === MODALS.DOMAIN_NOT_CONFIGURED && <ModalDomainNotConfigured hideModal={handleCloseModal} isPreChat />}
      {modalToShow === MODALS.DUPLICATE_ORDERS && <DuplicateOrdersModal hideModal={handleCloseModal} />}
      <TicketUpgradeVpsModal />
    </>
  );
};

export default withErrorBoundary(PreChat, 'PreChat');
